import type { KokoroVoiceId } from './voiceMapping';

/**
 * Self-hosted replica of kokoro-js's internal phonemize pipeline. Keeps
 * Stage 8's direct-ONNX path in sync with the tokens kokoro-js would feed
 * its model. Based on the kokoro-js 1.2.x bundle's `m(text, lang)` function.
 */

function normaliseNumericWord(e: string): string {
  if (e.includes('.')) return e;
  if (e.includes(':')) {
    const parts = e.split(':').map(Number);
    const a = parts[0];
    const t = parts[1];
    if (t === 0) return `${a} o'clock`;
    if (t < 10) return `${a} oh ${t}`;
    return `${a} ${t}`;
  }
  const a = parseInt(e.slice(0, 4), 10);
  if (a < 1100 || a % 1000 < 10) return e;
  const t = e.slice(0, 2);
  const r = parseInt(e.slice(2, 4), 10);
  const n = e.endsWith('s') ? 's' : '';
  if (a % 1000 >= 100 && a % 1000 <= 999) {
    if (r === 0) return `${t} hundred${n}`;
    if (r < 10) return `${t} oh ${r}${n}`;
  }
  return `${t} ${r}${n}`;
}

function normaliseCurrency(e: string): string {
  const unit = e[0] === '$' ? 'dollar' : 'pound';
  if (isNaN(Number(e.slice(1)))) return `${e.slice(1)} ${unit}s`;
  if (!e.includes('.')) {
    const suffix = e.slice(1) === '1' ? '' : 's';
    return `${e.slice(1)} ${unit}${suffix}`;
  }
  const [whole, frac] = e.slice(1).split('.');
  const cents = parseInt(frac.padEnd(2, '0'), 10);
  const suffix = whole === '1' ? '' : 's';
  const subUnitName =
    e[0] === '$'
      ? cents === 1
        ? 'cent'
        : 'cents'
      : cents === 1
        ? 'penny'
        : 'pence';
  return `${whole} ${unit}${suffix} and ${cents} ${subUnitName}`;
}

function normaliseDecimal(e: string): string {
  const [whole, frac] = e.split('.');
  return `${whole} point ${frac.split('').join(' ')}`;
}

function normalise(text: string): string {
  return text
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/\u00ab/g, '\u201c')
    .replace(/\u00bb/g, '\u201d')
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/\(/g, '\u00ab')
    .replace(/\)/g, '\u00bb')
    .replace(/\u3001/g, ', ')
    .replace(/\u3002/g, '. ')
    .replace(/\uff01/g, '! ')
    .replace(/\uff0c/g, ', ')
    .replace(/\uff1a/g, ': ')
    .replace(/\uff1b/g, '; ')
    .replace(/\uff1f/g, '? ')
    .replace(/[^\S \n]/g, ' ')
    .replace(/  +/, ' ')
    .replace(/(?<=\n) +(?=\n)/g, '')
    .replace(/\bD[Rr]\.(?= [A-Z])/g, 'Doctor')
    .replace(/\b(?:Mr\.|MR\.(?= [A-Z]))/g, 'Mister')
    .replace(/\b(?:Ms\.|MS\.(?= [A-Z]))/g, 'Miss')
    .replace(/\b(?:Mrs\.|MRS\.(?= [A-Z]))/g, 'Mrs')
    .replace(/\betc\.(?! [A-Z])/gi, 'etc')
    .replace(/\b(y)eah?\b/gi, "$1e'a")
    .replace(
      /\d*\.\d+|\b\d{4}s?\b|(?<!:)\b(?:[1-9]|1[0-2]):[0-5]\d\b(?!:)/g,
      normaliseNumericWord,
    )
    .replace(/(?<=\d),(?=\d)/g, '')
    .replace(
      /[$£]\d+(?:\.\d+)?(?: hundred| thousand| (?:[bm]|tr)illion)*\b|[$£]\d+\.\d\d?\b/gi,
      normaliseCurrency,
    )
    .replace(/\d*\.\d+/g, normaliseDecimal)
    .replace(/(?<=\d)-(?=\d)/g, ' to ')
    .replace(/(?<=\d)S/g, ' S')
    .replace(/(?<=[BCDFGHJ-NP-TV-Z])'?s\b/g, "'S")
    .replace(/(?<=X')S\b/g, 's')
    .replace(/(?:[A-Za-z]\.){2,} [a-z]/g, (m) => m.replace(/\./g, '-'))
    .replace(/(?<=[A-Z])\.(?=[A-Z])/gi, '-')
    .trim();
}

const PUNCT_CHARS = ';:,.!?\u00a1\u00bf\u2014\u2026"\u00ab\u00bb\u201c\u201d(){}[]';
const PUNCT_ESCAPED = PUNCT_CHARS.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const PUNCT_RE = new RegExp(`(\\s*[${PUNCT_ESCAPED}]+\\s*)+`, 'g');

function segmentText(text: string): Array<{ match: boolean; text: string }> {
  const out: Array<{ match: boolean; text: string }> = [];
  let r = 0;
  for (const m of text.matchAll(PUNCT_RE)) {
    const a = m[0];
    const idx = m.index ?? 0;
    if (r < idx) out.push({ match: false, text: text.slice(r, idx) });
    if (a.length > 0) out.push({ match: true, text: a });
    r = idx + a.length;
  }
  if (r < text.length) out.push({ match: false, text: text.slice(r) });
  return out;
}

export async function phonemizeForKokoro(
  text: string,
  voiceId: KokoroVoiceId,
): Promise<string> {
  const lang: 'a' | 'b' = voiceId.startsWith('b') ? 'b' : 'a';
  const espeakLang = lang === 'a' ? 'en-us' : 'en';
  const normalised = normalise(text);
  const segments = segmentText(normalised);

  const mod = (await import('phonemizer')) as unknown as {
    phonemize: (t: string, l?: string) => Promise<string[]>;
  };
  const parts = await Promise.all(
    segments.map(async (s) => {
      if (s.match) return s.text;
      const ph = await mod.phonemize(s.text, espeakLang);
      return ph.join(' ');
    }),
  );
  let joined = parts.join('');

  // Kokoro-specific post-processing (copied verbatim from the kokoro-js bundle).
  joined = joined
    .replace(/k\u0259k\u02c8o\u02d0\u0279o\u028a/g, 'k\u02c8o\u028ak\u0259\u0279o\u028a')
    .replace(/k\u0259k\u02c8\u0254\u02d0\u0279\u0259\u028a/g, 'k\u02c8\u0259\u028ak\u0259\u0279\u0259\u028a')
    .replace(/\u02b2/g, 'j')
    .replace(/r/g, '\u0279')
    .replace(/x/g, 'k')
    .replace(/\u026c/g, 'l')
    .replace(/(?<=[a-z\u0279\u02d0])(?=h\u02c8\u028cnd\u0279\u026ad)/g, ' ')
    .replace(/ z(?=[;:,.!?\u00a1\u00bf\u2014\u2026"\u00ab\u00bb\u201c\u201d ]|$)/g, 'z');

  if (lang === 'a') {
    joined = joined.replace(/(?<=n\u02c8a\u026an)ti(?!\u02d0)/g, 'di');
  }
  return joined.trim();
}
